// =============================================================================
// usePalletPricing — shared rate-card / tare data hooks for pallet creation.
//
// These wrap the queries NewPalletForm used to declare inline, so the single
// create form, the bulk "from a line" dialog, and the "weigh later" dialog all
// read the SAME data through the SAME query keys (shared RQ cache, no drift).
// The tare → net math itself lives in src/lib/palletCompute.ts.
//
// Query keys are kept byte-identical to the originals so existing cache entries
// (and any other readers) keep hitting.
// =============================================================================
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useVarietyFamilies } from '@/lib/useVarietyFamilies'
import { toTareComponent, shellWeighability, type TareComponent } from '@/lib/palletCompute'
import { withTimeout } from '@/lib/saveTimeout'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { fetchAllPagedIn } from '@/lib/inChunks'
import { fetchAllPaged, type PageResult } from '@/lib/fetchAllPaged'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { useCanReadRateCards } from '@/hooks/useCanReadRateCards'
import { usePermissions } from '@/lib/usePermissions'
import { unweighableReason, unweighableByCode, type WeighCandidate, type UnweighablePack } from '@/lib/unweighableReason'

// A saved pack spec ("line", mig 890 line_presets). mark resolves product_id at
// create time — a frozen product_id would mis-bill when rate cards are re-tagged.
export interface LinePreset {
  preset_id:             string
  /** GS1 GTIN-14 for this pack-line (trade item). Server-minted (mig 974). */
  gtin?:                 string | null
  /** Optimistic-concurrency version (mig 890). Optional — only the admin editor selects it. */
  version?:              number | null
  name:                  string
  commodity:             string
  // FK to commodities (mig 967) — the canonical link the run picker matches
  // through; `commodity` is the snapshot name (rename- and case-safe).
  commodity_id:          string | null
  variety:               string | null
  channel:               'EXPORT' | 'LOCAL' | null
  pallet_type:           string | null
  pack_type:             string
  grade:                 string | null
  size_code:             string | null
  mark:                  string | null
  target_market:         string | null
  destination:           string | null
  regime_code:           string | null
  lug_type:              string | null
  lug_count:             number | null
  default_carton_count:  number | null
  // Mig 908 — full export-label / consignment fields (defaults stamped onto each
  // pallet created from the line). organisation/inventory_code/product_group/
  // product_char/stack_type/target_country/target_region resolve pack_ref_codes;
  // pick_reference → pallet.pick_no; country_of_origin defaults 'ZA'.
  organisation:          string | null
  inventory_code:        string | null
  country_of_origin:     string | null
  product_group:         string | null
  product_char:          string | null
  pick_reference:        string | null
  stack_type:            string | null
  target_country:        string | null
  target_region:         string | null
  sort_order:            number
  is_active:             boolean
}

export interface PackVariant {
  product_id:                string
  product_code:              string
  variant:                   string
  name:                      string
  category:                  string
  fruit_type:                string | null
  target_weight_per_unit_kg: number | null
  min_weight_per_unit_kg:    number | null
  allowed_grades:            string[] | null
}

export interface TareRecipe {
  standard:   number
  components: TareComponent[]
  /** Weight specs from the narrow read only (a floor seat reads no rate card); the dialog falls back to them for the under-weight check. */
  weights?:   { min_weight_per_unit_kg: number | null; target_weight_per_unit_kg: number | null } | null
}

export interface PackLine {
  component_id:       string
  component_name:     string
  default_stock_id:   string
  default_stock_name: string
  options:            { stock_item_id: string; item_name: string }[]
}

// ---------------------------------------------------------------------------
// Variants for a pack code, filtered to the run's variety OR commodity family.
//   allVariants — every active variant of the pack code (NULL = no pack code).
//   variants    — filtered by fruitTypeMatchesRun (a fruit_type tag may be a
//                 cultivar OR a commodity name); NULL-fruit_type kept; an
//                 empty list means "no matching rate card" (don't guess wrong).
//   loaded      — the fetch SUCCEEDED. Gate every "there is no rate card"
//                 conclusion on this.
//
// ⚠️ An empty `allVariants` does NOT mean the catalogue is empty. The `= []`
// default below also fires while the query is pending and after it FAILS
// (expired JWT, offline blip, timeout — none of which reach the caller,
// because the error stays on the query object). Anchor, 2026-08-07: the create
// form read that empty array as fact and told the packhouse "There's no active
// rate-card product for pack code ER250 — add the product in Config →
// Products", on a pack code that was correctly configured and already had 65
// pallets billed against it. The operator was blocked for the rest of the
// shift chasing a config problem that did not exist. Hence `loaded`.
// ---------------------------------------------------------------------------
export function useVariantsForPack(
  packType:     string | null | undefined,
  runVariety:   string | null | undefined,
  runCommodity: string | null | undefined,
) {
  const { fruitTypeMatchesRun } = useVarietyFamilies()
  const tenantId = useActiveTenantId()
  const { data: allVariants = [], ...rest } = useQuery({
    // The RPC answers for the active business, so the business is in the key
    // (appended: prefix invalidations on the code still match).
    queryKey: ['financial-products-by-code', packType, tenantId],
    enabled: !!packType && !!tenantId,
    queryFn: async () => {
      // pallet_pack_products (mig 20261298000000): the non-price fields of this
      // business's active products, readable by a floor seat that makes or
      // weighs pallets. A direct financial_products read returns no rows below
      // manager (RM9), which read as "No rate card" on every pack.
      const { data, error } = await supabase
        .rpc('pallet_pack_products', { p_product_code: packType! })
        .order('variant')
      if (error) throw error
      return (data ?? []) as PackVariant[]
    },
  })

  const variants = useMemo<PackVariant[]>(() => {
    if (!runVariety?.trim() && !runCommodity?.trim()) return allVariants
    return allVariants.filter(p => fruitTypeMatchesRun(p.fruit_type, runVariety, runCommodity))
  }, [allVariants, runVariety, runCommodity, fruitTypeMatchesRun])

  // `loaded` last-wins over nothing in `rest` (react-query has no such key),
  // and is deliberately isSuccess — NOT `!isPending` — so an errored query
  // that still holds stale data reads as "don't trust this as the answer".
  return { allVariants, variants, loaded: rest.isSuccess, ...rest }
}

// ---------------------------------------------------------------------------
// Every ACTIVE rate-card product, slimmed to the fields the pallet form gates
// on. One row per (product_code, variant), so a code appears as many times as
// it has variants.
//
// Key is byte-identical to the query NewPalletForm used to declare inline, so
// the form and usePalletFieldOptions share ONE fetch and can't disagree about
// which pack codes are costed.
//
// Two consumers, two questions:
//   - mig 481 grade gating  → allowed_grades / fruit_type (the form)
//   - "can this pack code produce a pallet at all?" → product_code presence
//     (usePalletFieldOptions → packTypesWithoutRateCard)
//
// ⚠️ `loaded` is isSuccess, NOT `!isPending` and NEVER `rows.length > 0` — the
// same rule as useVariantsForPack above. An empty array is what a PENDING or
// FAILED read looks like too, and callers here disable UI on it: judging on
// emptiness would grey out every pack pill on a blip. Fail OPEN.
//
// Read through pallet_pack_products (mig 20261298000000), which returns no
// prices and admits the rate-card read set PLUS any seat with Pallets or
// Coldroom edit, so a floor seat that makes pallets can tell which codes are
// costed. `canRead` mirrors that gate; below it the RPC refuses, so the read
// is not made and `loaded` stays false: null always means "can't tell".
// ---------------------------------------------------------------------------
interface ActiveProductRule {
  product_code:   string
  fruit_type:     string | null
  allowed_grades: string[] | null
}

/** Who pallet_pack_products admits (mig 20261298000000): the rate-card read
 *  set, or a seat with Pallets or Coldroom edit. `known` is false until the
 *  permission map has loaded, so nobody is told "can't read" too early. */
function useCanReadPackProducts(): { canRead: boolean; known: boolean } {
  const canReadCards = useCanReadRateCards()
  const { can, isSuccess } = usePermissions()
  const canRead = canReadCards || can('pallets', 'edit') || can('coldroom', 'edit')
  return { canRead, known: canReadCards || isSuccess }
}

export function useActiveProductRules(enabled = true) {
  const { data: me } = useCurrentUser()
  const { canRead, known } = useCanReadPackProducts()
  const tenantId = useActiveTenantId()
  const { data: rules = [], ...rest } = useQuery({
    queryKey: ['financial-products-grade-rules', tenantId],
    enabled: enabled && canRead && !!tenantId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      // Paged: a tenant with >1000 active products must not be cut short.
      // pallet_pack_products returns only the caller's business, active rows.
      // ⚠️ The order column MUST be selected: PostgREST returns only the
      // selected columns from an RPC, then orders them, so ordering by an
      // unselected product_id fails with "column record.product_id does not exist".
      const res = await fetchAllPaged<ActiveProductRule>((from, to) => supabase
        .rpc('pallet_pack_products', {})
        .select('product_id, product_code, fruit_type, allowed_grades')
        .order('product_id')
        .range(from, to) as unknown as PromiseLike<PageResult<ActiveProductRule>>)
      if (res.error) throw new Error(res.error)
      return res.rows
    },
  })

  // The set of pack codes that have at least one active rate card. Membership
  // here is the precondition for a pallet: without a product there is no tare
  // recipe, and palletCompute refuses to produce a net weight.
  const codesWithRateCard = useMemo(
    () => new Set(rules.map(r => r.product_code)),
    [rules],
  )

  // cannotRead: known to be outside the gate. While the user or the permission map
  // is still loading it is false, so the form does not claim "cannot be checked"
  // before it knows who is asking.
  return { rules, codesWithRateCard, loaded: canRead && rest.isSuccess, canRead, cannotRead: !!me && known && !canRead, ...rest }
}

// ---------------------------------------------------------------------------
// Tare recipe for a variant. Returns ALL recipe lines (incl. ones missing tare)
// so the caller can refuse to save. Resolution: line_tare_* (mig 188) wins,
// stock_items.tare_* is the fallback; tare_variable lines (mig 869) are weighed.
// ---------------------------------------------------------------------------
// The weigher's read: SECURITY DEFINER, tenant-scoped, returns tare fields only (no prices).
type RpcTareRecipe = {
  units_per_pallet?: number; components?: TareComponent[]
  min_weight_per_unit_kg?: number | null; target_weight_per_unit_kg?: number | null
}

async function fetchTareViaRpc(productId: string): Promise<TareRecipe> {
  const { data, error } = await withTimeout(supabase.rpc('pallet_tare_recipe', { p_product_id: productId }), 'Pallets: tare recipe read')
  if (error) throw error
  const r = (data ?? {}) as RpcTareRecipe
  return {
    standard: Number(r.units_per_pallet) || 0,
    components: r.components ?? [],
    weights: { min_weight_per_unit_kg: r.min_weight_per_unit_kg ?? null, target_weight_per_unit_kg: r.target_weight_per_unit_kg ?? null },
  }
}

export function useTareRecipe(productId: string | null | undefined) {
  const tenantId = useActiveTenantId()
  return useQuery({
    queryKey: ['pallet-tare-recipe', productId, tenantId],
    enabled: !!productId && !!tenantId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<TareRecipe> => {
      const [prodRes, recipeRes] = await Promise.all([
        supabase.from('financial_products')
          .select('units_per_pallet')
          .eq('product_id', productId!)
          .maybeSingle(),
        supabase.from('product_recipes')
          .select('component_id, units_per_pallet, line_tare_weight_kg, line_tare_basis, cost_components!inner(name, stock_items(item, tare_weight_kg, tare_basis, tare_variable, stock_categories(name)))')
          .eq('product_id', productId!)
          .is('effective_to', null),
      ])
      // Throw on either error (RB1 D3), as usePackLines does: a failed read
      // came back as an empty recipe, which the form reads as "no recipe lines".
      if (prodRes.error)   throw prodRes.error
      if (recipeRes.error) throw recipeRes.error
      // A weighing seat below manager reads no rate card (row security returns ZERO rows, not an
      // error), so no product row means "ask the narrow read" (mig 20261183000000, U18 UF-04).
      // A manager's direct read finds the row and never gets here.
      if (!prodRes.data) return fetchTareViaRpc(productId!)
      const components: TareComponent[] = ((recipeRes.data ?? []) as any[]).map(toTareComponent)
      return {
        standard:   Number((prodRes.data as { units_per_pallet?: number } | null)?.units_per_pallet) || 0,
        components,
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Recipe lines that have operator-pickable alternatives (mig 114). Only lines
// with at least one alternative are returned (the rest use the recipe default).
// Alternatives grouped by component. Kept out of the queryFn so the fetch,
// the grouping and the line build are each small enough to read.
type AltRow = { component_id: string; stock_item_id: string; stock_items: { item: string } | { item: string }[] }
function groupAlternatives(rows: AltRow[]): Map<string, { stock_item_id: string; item_name: string }[]> {
  const byComponent = new Map<string, { stock_item_id: string; item_name: string }[]>()
  for (const r of rows) {
    const itemName = Array.isArray(r.stock_items) ? r.stock_items[0]?.item ?? '?' : r.stock_items?.item ?? '?'
    byComponent.set(r.component_id, [...(byComponent.get(r.component_id) ?? []),
      { stock_item_id: r.stock_item_id, item_name: itemName }])
  }
  return byComponent
}

type RecipeRow = { component_id: string; component_name: string; stock_item_id: string | null; stock_item_name: string | null }
/** Only recipe lines that actually offer the operator a choice become PackLines. */
function toPackLines(recipe: RecipeRow[], altsByComponent: Map<string, { stock_item_id: string; item_name: string }[]>): PackLine[] {
  const lines: PackLine[] = []
  for (const r of recipe) {
    const alts = r.stock_item_id ? altsByComponent.get(r.component_id) ?? [] : []
    if (!r.stock_item_id || alts.length === 0) continue
    const defaultName = r.stock_item_name ?? r.component_name
    lines.push({
      component_id:       r.component_id,
      component_name:     r.component_name,
      default_stock_id:   r.stock_item_id,
      default_stock_name: defaultName,
      options:            [{ stock_item_id: r.stock_item_id, item_name: defaultName }, ...alts],
    })
  }
  return lines
}

// ---------------------------------------------------------------------------
export function usePackLines(productId: string | null | undefined) {
  return useQuery({
    queryKey: ['pallet-pack-lines', productId],
    enabled: !!productId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<PackLine[]> => {
      const [recipeRes, altRes] = await Promise.all([
        supabase.from('current_product_recipes')
          .select('component_id, component_name, stock_item_id, stock_item_name')
          .eq('product_id', productId!),
        supabase.from('recipe_alternatives')
          .select('component_id, stock_item_id, sort_order, stock_items!inner(item)')
          .eq('product_id', productId!)
          .eq('is_active', true)
          .order('sort_order'),
      ])
      // Throw on either error. Both were dropped, so a failed read produced an
      // EMPTY pack-line list — and Pallets.tsx treats "no pack lines" as "this
      // product needs no stock-item pick", so the mandatory pick was skipped
      // and the pallet saved against no packaging at all. An empty list must
      // mean "this product genuinely has none", never "we could not look".
      if (recipeRes.error) throw recipeRes.error
      if (altRes.error)    throw altRes.error
      return toPackLines(
        (recipeRes.data ?? []) as RecipeRow[],
        groupAlternatives((altRes.data ?? []) as AltRow[]),
      )
    },
  })
}

// ---------------------------------------------------------------------------
// Pack codes whose rate card can NEVER be weighed (F21 UF-06).
//
// The chips already strike codes with no active product. A code whose card
// exists but has a cost line with no tare, no units per pallet, or no lines
// passed that check and was refused only AFTER the operator weighed the pallet
// and typed the cartons (the tare check needs a variant + cartons). This asks
// the same question shellWeighability asks, for every offered code up front:
// one read of the active products, one of their current recipe lines.
//
// A code is unweighable only if it has >= 1 candidate for this run (folded with
// fruitTypeMatchesRun, exactly as useVariantsForPack does) and EVERY candidate
// fails. One weighable variant is enough to pack on.
//
// ⚠️ Fail OPEN, same rule as uncostedPackTypes / useActiveProductRules: the map
// is EMPTY unless the query SUCCEEDED. A pending or failed read looks like "no
// problems" here, never like "every card is broken".
// ---------------------------------------------------------------------------
const NO_UNWEIGHABLE: ReadonlyMap<string, UnweighablePack> = new Map()

export function useUnweighablePackCodes(
  offered:      readonly string[],
  runVariety:   string | null | undefined,
  runCommodity: string | null | undefined,
  enabled:      boolean,
) {
  const tenantId = useActiveTenantId()
  const { fruitTypeMatchesRun } = useVarietyFamilies()
  const codes = useMemo(() => Array.from(new Set(offered)).sort(), [offered])

  const { data: candidates, isSuccess } = useQuery({
    queryKey: ['unweighable-pack-candidates', tenantId, codes],
    enabled: enabled && !!tenantId && codes.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<WeighCandidate[]> => {
      const prods = await fetchAllPagedIn<{ product_id: string; product_code: string; fruit_type: string | null; units_per_pallet: number | null }, string>(
        codes,
        (chunk, from, to) => supabase.from('financial_products')
          .select('product_id, product_code, fruit_type, units_per_pallet')
          .in('product_code', chunk).eq('is_active', true)
          .order('product_id').range(from, to),
      )
      if (prods.error) throw new Error(prods.error)
      const ids = prods.rows.map(p => p.product_id)
      const recipes = await fetchAllPagedIn<any, string>(
        ids,
        (chunk, from, to) => supabase.from('product_recipes')
          .select('product_id, component_id, units_per_pallet, line_tare_weight_kg, line_tare_basis, cost_components!inner(name, stock_items(item, tare_weight_kg, tare_basis, tare_variable, stock_categories(name)))')
          .in('product_id', chunk).is('effective_to', null)
          .order('product_id').order('component_id').range(from, to),
      )
      if (recipes.error) throw new Error(recipes.error)
      const byProduct = new Map<string, TareComponent[]>()
      for (const r of recipes.rows) {
        byProduct.set(r.product_id, [...(byProduct.get(r.product_id) ?? []), toTareComponent(r)])
      }
      return prods.rows.map(p => ({
        product_id:       p.product_id,
        product_code:     p.product_code,
        fruit_type:       p.fruit_type,
        units_per_pallet: Number(p.units_per_pallet) || 0,
        components:       byProduct.get(p.product_id) ?? [],
      }))
    },
  })

  const unweighable = useMemo<ReadonlyMap<string, UnweighablePack>>(() => {
    if (!isSuccess || !candidates) return NO_UNWEIGHABLE
    // A blank run (no variety, no commodity) matches everything, like
    // useVariantsForPack: skip the fruit fold.
    const blankRun = !runVariety?.trim() && !runCommodity?.trim()
    const sameCount = (code: string) => candidates.filter(c => c.product_code === code).length
    return unweighableByCode(
      candidates,
      c => blankRun || fruitTypeMatchesRun(c.fruit_type, runVariety, runCommodity),
      c => {
        const n = sameCount(c.product_code)
        const w = shellWeighability({
          rateCardLoaded: true, allVariantCount: n, varietyVariantCount: n,
          productId: c.product_id, standard: c.units_per_pallet, components: c.components,
          packType: c.product_code, runVariety: runVariety ?? '',
        })
        return w.ok ? null : unweighableReason(c.components, c.units_per_pallet)
      },
    )
  }, [isSuccess, candidates, fruitTypeMatchesRun, runVariety, runCommodity])

  return { unweighable, loaded: isSuccess }
}
